// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useReservation } from '../../contexts/ReservationContext.tsx';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import {
  fetchSalaInfo,
  fetchRestaurantSettings
} from '../../services/api.ts';
import { toast } from 'react-toastify';
import '../../styles/reservation-form.css';
import {
  CapacityWarningInfo,
  toNumberOrNull,
  isCanceledStatus,
  getGuestCapacityWarning,
  validateGuestCount,
  buildReservationFormData,
} from './reservationForm.utils.ts';
import useReservationFormShifts from '../../hooks/useReservationFormShifts.ts';
import useReservationFormCrm from '../../hooks/useReservationFormCrm.ts';
import useReservationFormSubmit from '../../hooks/useReservationFormSubmit.ts';
import ReservationFormCrmSearchModal from './ReservationFormCrmSearchModal.tsx';
import ReservationFormClientConfirmationModal from './ReservationFormClientConfirmationModal.tsx';
import ReservationFormCancelConfirmModal from './ReservationFormCancelConfirmModal.tsx';
import ReservationFormScheduleFields from './ReservationFormScheduleFields.tsx';
import ReservationFormCapacityFields from './ReservationFormCapacityFields.tsx';
import ReservationFormClientFields from './ReservationFormClientFields.tsx';
import ReservationFormHeader from './ReservationFormHeader.tsx';
import ReservationFormActions from './ReservationFormActions.tsx';
import { usesTableMap } from '../../utils/roomMode.ts';

interface ReservationFormProps {
  reservation?: any;
  onClose: () => void;
  selectedDate: Date;
  preselectedSalaId?: string | number;
  onReservationCancelled?: (reservationId: string | number) => Promise<void> | void;
}

const ReservationForm: React.FC<ReservationFormProps> = ({
  reservation,
  onClose,
  selectedDate,
  preselectedSalaId,
  onReservationCancelled
}) => {
  const {
    createReservation,
    updateReservation,
    reservations
  } = useReservation();
  const { floors } = useRestaurant();
  const preselectedSalaIdValue = preselectedSalaId !== undefined && preselectedSalaId !== null
    ? String(preselectedSalaId)
    : '';

  const allTables = useMemo(
    () =>
      floors.flatMap(floor =>
        floor.tables.map(table => ({ ...table, floorId: floor.id, floorName: floor.name }))
      ),
    [floors]
  );

  // Estados para informações da sala
  const [salaInfo, setSalaInfo] = useState<any>(null);
  const [restaurantSettings, setRestaurantSettings] = useState<any>({});
  const usarMapaMesas = usesTableMap(salaInfo, restaurantSettings?.reservo_usar_mapa_mesas !== false);
  const salaUsaMesas = usarMapaMesas && salaInfo?.modo_layout === true;

  // Estado para validações
  const [guestValidationError, setGuestValidationError] = useState<string>('');
  const [guestCapacityWarning, setGuestCapacityWarning] = useState<CapacityWarningInfo | null>(null);
  const [guestCapacityWarningConfirmed, setGuestCapacityWarningConfirmed] = useState(false);

  // Estado para disponibilidade da mesa selecionada
  const [tableAvailabilityStatus, setTableAvailabilityStatus] = useState<'available' | 'unavailable' | null>(null);
  const [tableAvailabilityMessage, setTableAvailabilityMessage] = useState<string>('');

  const isEditing = !!reservation;
  const [formData, setFormData] = useState(() => buildReservationFormData({
    reservation: isEditing ? reservation : undefined,
    selectedDate,
    preselectedSalaId: preselectedSalaIdValue,
  }));

  const applyTimeFields = useCallback((fields: {
    turno: number | undefined;
    horario_id: number | string | null;
    time: string;
  }) => {
    setFormData((prev) => ({
      ...prev,
      turno: fields.turno,
      horario_id: fields.horario_id,
      time: fields.time,
    }));
  }, []);

  const onSelectCrmCustomer = useCallback((fields: {
    name: string;
    phone: string;
    email: string;
  }) => {
    setFormData((current) => ({
      ...current,
      name: fields.name || current.name,
      phone: fields.phone || current.phone,
      email: fields.email || current.email,
    }));
  }, []);

  const {
    crmSearchOpen,
    crmSearchTerm,
    setCrmSearchTerm,
    crmSearchResults,
    crmSearchLoading,
    crmSearchError,
    openCrmSearch,
    closeCrmSearch,
    handleCrmSearch,
    selectCrmCustomer,
  } = useReservationFormCrm({ onSelectCustomer: onSelectCrmCustomer });

  const {
    availableTimeOptions,
    loadingShifts,
    noAvailableShiftsMessage,
    blockedTurnos,
    blockedHorarioIds,
    blockedShiftNotice,
    setBlockedShiftNotice,
    clearShiftState,
    loadAvailableShiftsAndTimes,
    resolveSelectedTimeOption,
  } = useReservationFormShifts({
    formDate: formData.date,
    onApplyTimeFields: applyTimeFields,
  });

  const { minGuestsLimit, maxGuestsLimit } = useMemo(() => {
    if (!salaInfo || salaUsaMesas) {
      return { minGuestsLimit: undefined, maxGuestsLimit: undefined };
    }

    return {
      minGuestsLimit: toNumberOrNull(salaInfo.min_pessoas_por_reserva) ?? undefined,
      maxGuestsLimit: toNumberOrNull(salaInfo.max_pessoas_por_reserva) ?? undefined
    };
  }, [salaInfo, salaUsaMesas]);

  const maxAdvanceDays = useMemo(() => {
    const raw = restaurantSettings?.reservo_prazo_reserva_dias;
    const parsed = raw !== null && raw !== undefined && raw !== ''
      ? Number.parseInt(String(raw), 10)
      : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [restaurantSettings]);

  const maxDateValue = useMemo(() => {
    if (!maxAdvanceDays) return undefined;
    const maxDate = new Date();
    maxDate.setHours(0, 0, 0, 0);
    maxDate.setDate(maxDate.getDate() + maxAdvanceDays);
    return maxDate.toLocaleDateString('en-CA');
  }, [maxAdvanceDays]);

  const maxAdvanceDate = useMemo(() => {
    if (!maxAdvanceDays) return null;
    const maxDate = new Date();
    maxDate.setHours(0, 0, 0, 0);
    maxDate.setDate(maxDate.getDate() + maxAdvanceDays);
    return maxDate;
  }, [maxAdvanceDays]);
  const requiresRestaurantConfirmation = Boolean(restaurantSettings?.reservo_confirmacao_restaurante);
  const requiresClientConfirmation = Boolean(restaurantSettings?.reservo_confirmacao_cliente);

  // Função para carregar informações da sala
  const loadSalaInfo = async (salaId: string) => {
    if (!salaId) {
      setSalaInfo(null);
      return;
    }

    try {
      const info = await fetchSalaInfo(salaId);
      setSalaInfo(info);
    } catch (error) {
      console.error('Erro ao carregar informações da sala:', error);
      setSalaInfo(null);
    }
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await fetchRestaurantSettings();
        setRestaurantSettings(data || {});
      } catch (error) {
        console.error('Erro ao carregar configurações do restaurante:', error);
      }
    };

    loadSettings();
  }, []);

  const runGuestValidation = useCallback(
    (guests: number, tableIds?: Array<string | number>) => validateGuestCount({
      salaInfo,
      salaUsaMesas,
      guests,
      tableIds,
      allTables,
      fallbackTableIds: formData.tableIds,
    }),
    [salaInfo, salaUsaMesas, allTables, formData.tableIds]
  );

  useEffect(() => {
    if (!salaInfo) {
      setGuestValidationError('');
      setGuestCapacityWarning(null);
      return;
    }

    const guestCount = toNumberOrNull(formData.guests) ?? 0;
    const validationMessage = runGuestValidation(guestCount, formData.tableIds);
    const warning = getGuestCapacityWarning(validationMessage);
    setGuestCapacityWarning(warning);
    setGuestValidationError(warning ? '' : validationMessage);
  }, [formData.guests, formData.tableIds, salaInfo, runGuestValidation]);

  useEffect(() => {
    setGuestCapacityWarningConfirmed(false);
  }, [guestCapacityWarning?.title, guestCapacityWarning?.message]);

  // Update form data when reservation changes (for editing different reservations)
  useEffect(() => {
    if (isEditing && reservation) {
      setFormData(buildReservationFormData({
        reservation,
        selectedDate,
        preselectedSalaId: preselectedSalaIdValue,
      }));
    } else if (!isEditing) {
      const nextSalaId = preselectedSalaIdValue;
      setFormData(buildReservationFormData({
        selectedDate,
        preselectedSalaId: nextSalaId,
      }));
      setSalaInfo(null);
      setGuestValidationError('');
      setGuestCapacityWarning(null);
      setGuestCapacityWarningConfirmed(false);
      if (nextSalaId) {
        loadSalaInfo(nextSalaId);
        loadAvailableShiftsAndTimes(nextSalaId, { date: selectedDate });
      } else {
        clearShiftState();
      }
    }
  // Omite loadSalaInfo/loadAvailableShiftsAndTimes: reset do form ao trocar reserva/data/sala, sem recriar o efeito.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservation, isEditing, selectedDate, preselectedSalaIdValue]);

  useEffect(() => {
    if (!isEditing || !reservation?.sala_id || !reservation?.data) {
      return;
    }

    const reservationDate = new Date(reservation.data + 'T00:00:00');
    const normalizedTime = reservation.hora
      ? reservation.hora.split(':').slice(0, 2).join(':')
      : null;

    const loadInitialData = async () => {
      await loadSalaInfo(reservation.sala_id);
      await loadAvailableShiftsAndTimes(reservation.sala_id, {
        date: reservationDate,
        keepCurrentSelection: true,
        preselectedHorarioId: reservation.horario_id ?? null,
        preselectedTime: normalizedTime
      });
    };

    loadInitialData();
  }, [
    isEditing,
    reservation?.id,
    reservation?.sala_id,
    reservation?.data,
    reservation?.hora,
    reservation?.horario_id,
    loadAvailableShiftsAndTimes
  ]);

  useEffect(() => {
    const selectedTableIds = formData.tableIds.map((tableId: string) => String(tableId));
    const salaId = formData.sala_id ? String(formData.sala_id) : '';
    const time = formData.time;

    if (!salaUsaMesas || selectedTableIds.length === 0 || !salaId || !time) {
      setTableAvailabilityStatus(null);
      setTableAvailabilityMessage('');
      return;
    }

    const formattedDate = formData.date.toLocaleDateString('en-CA');

    const conflictingReservation = reservations.find(reserva => {
      if (!reserva) return false;
      if (reservation && reserva.id && reservation.id && String(reserva.id) === String(reservation.id)) {
        return false;
      }
      const reservaMesaIds = Array.isArray((reserva as any).mesa_ids) && (reserva as any).mesa_ids.length > 0
        ? (reserva as any).mesa_ids
        : (((reserva as any).mesa_id !== null && (reserva as any).mesa_id !== undefined) ? [(reserva as any).mesa_id] : []);
      const hasMatchingTable = reservaMesaIds.some((mesaId: string | number) => (
        selectedTableIds.some((selectedTableId) => String(selectedTableId) === String(mesaId))
      ));
      if (!hasMatchingTable) return false;
      if (reserva.data !== formattedDate) return false;
      if (isCanceledStatus((reserva as any).status)) {
        return false;
      }

      const normalizedTime = reserva.hora ? reserva.hora.split(':').slice(0, 2).join(':') : '';
      if (normalizedTime === time) return true;

      // Fallback: compare turno/horario_id if available
      if (reserva.horario_id && formData.horario_id) {
        return String(reserva.horario_id) === String(formData.horario_id);
      }

      return false;
    });

    if (conflictingReservation) {
      setTableAvailabilityStatus('unavailable');
      const reservedBy = conflictingReservation.nome_cliente
        ? ` Reservada para ${conflictingReservation.nome_cliente}.`
        : '';
      setTableAvailabilityMessage(`Uma ou mais mesas estão indisponíveis para o horário selecionado.${reservedBy}`.trim());
    } else {
      setTableAvailabilityStatus('available');
      setTableAvailabilityMessage('Mesas disponíveis para o horário selecionado.');
    }
  }, [
    formData.tableIds,
    formData.sala_id,
    formData.time,
    formData.date,
    formData.horario_id,
    reservation?.id,
    reservation,
    reservations,
    salaUsaMesas
  ]);

  const selectedHorarioId = toNumberOrNull(formData.horario_id);
  const selectedTurnoNumber = toNumberOrNull(formData.turno);
  const selectedTimeOption = useMemo(() => {
    if (!formData.time || availableTimeOptions.length === 0) return null;

    if (selectedHorarioId !== null) {
      const byTimeAndHorario = availableTimeOptions.find((option) => (
        option.time === formData.time
        && option.horario_id !== null
        && option.horario_id === selectedHorarioId
      ));
      if (byTimeAndHorario) return byTimeAndHorario;
    }

    if (selectedTurnoNumber !== null) {
      const byTimeAndTurno = availableTimeOptions.find((option) => (
        option.time === formData.time
        && option.turno !== null
        && option.turno === selectedTurnoNumber
      ));
      if (byTimeAndTurno) return byTimeAndTurno;
    }

    return availableTimeOptions.find((option) => option.time === formData.time) || null;
  }, [availableTimeOptions, formData.time, selectedHorarioId, selectedTurnoNumber]);

  const selectedTimeValue = selectedTimeOption?.value || '';
  const normalizedHorarioId = selectedTimeOption?.horario_id ?? selectedHorarioId;
  const isSelectedSlotBlocked =
    (selectedTurnoNumber !== null && !Number.isNaN(selectedTurnoNumber) && blockedTurnos.includes(selectedTurnoNumber)) ||
    (normalizedHorarioId !== null && !Number.isNaN(normalizedHorarioId) && blockedHorarioIds.includes(normalizedHorarioId));

  const reservedTableIdsForSelectedSlot = useMemo(() => {
    const salaId = formData.sala_id ? String(formData.sala_id) : '';
    const time = formData.time ? String(formData.time) : '';
    if (!salaId || !time) return new Set<string>();

    const formattedDate = formData.date.toLocaleDateString('en-CA');
    const currentReservationId = reservation?.id ? String(reservation.id) : '';
    const selectedHorarioIdRaw = formData.horario_id !== null && formData.horario_id !== undefined && formData.horario_id !== ''
      ? String(formData.horario_id)
      : '';

    const reservedIds = new Set<string>();
    reservations.forEach((reserva: any) => {
      if (!reserva || isCanceledStatus(reserva?.status)) return;
      if (currentReservationId && reserva?.id && String(reserva.id) === currentReservationId) return;
      const reservaMesaIds = Array.isArray(reserva?.mesa_ids) && reserva.mesa_ids.length > 0
        ? reserva.mesa_ids
        : (reserva?.mesa_id ? [reserva.mesa_id] : []);
      if (reservaMesaIds.length === 0) return;
      if (String(reserva.sala_id) !== salaId) return;
      if (reserva.data !== formattedDate) return;

      const reservationTime = reserva.hora ? String(reserva.hora).split(':').slice(0, 2).join(':') : '';
      const sameTime = reservationTime === time;
      const sameHorario =
        selectedHorarioIdRaw
        && reserva?.horario_id !== null
        && reserva?.horario_id !== undefined
        && String(reserva.horario_id) === selectedHorarioIdRaw;

      if (sameTime || sameHorario) {
        reservaMesaIds.forEach((mesaId: string | number) => {
          reservedIds.add(String(mesaId));
        });
      }
    });

    return reservedIds;
  }, [
    formData.sala_id,
    formData.time,
    formData.date,
    formData.horario_id,
    reservation?.id,
    reservations
  ]);

  useEffect(() => {
    if (!formData.sala_id) {
      setBlockedShiftNotice('');
      return;
    }

    if (isSelectedSlotBlocked) {
      setBlockedShiftNotice('O horário selecionado está bloqueado para reservas.');
    } else {
      setBlockedShiftNotice('');
    }
  }, [formData.sala_id, isSelectedSlotBlocked, setBlockedShiftNotice]);

  const {
    showCancelConfirm,
    setShowCancelConfirm,
    isCanceling,
    showClientConfirmationChoice,
    isSavingReservation,
    pendingReservationHasPhone,
    closeClientConfirmationChoiceModal,
    handleClientConfirmationChoice,
    handleSubmit,
    handleCancelReservation,
  } = useReservationFormSubmit({
    isEditing,
    reservation,
    formData,
    salaInfo,
    salaUsaMesas,
    guestValidationError,
    guestCapacityWarning,
    guestCapacityWarningConfirmed,
    setGuestValidationError,
    setGuestCapacityWarning,
    runGuestValidation,
    selectedTurno: selectedTimeOption?.turno ?? selectedTurnoNumber,
    selectedHorarioId: selectedTimeOption?.horario_id ?? selectedHorarioId,
    requiresRestaurantConfirmation,
    requiresClientConfirmation,
    createReservation,
    updateReservation,
    onClose,
    onReservationCancelled,
  });

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const date = new Date(value + 'T00:00:00');
      if (maxAdvanceDate && date > maxAdvanceDate) {
        toast.warning(`Reservas podem ser feitas com até ${maxAdvanceDays} dias de antecedência.`);
        return;
      }
      setFormData(prev => ({ ...prev, date }));

      if (formData.sala_id) {
        loadAvailableShiftsAndTimes(formData.sala_id, {
          date,
          keepCurrentSelection: true,
          preselectedHorarioId: formData.horario_id,
          preselectedTime: formData.time || null
        });
      }
    }
  };
  
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    let newValue: any = type === 'checkbox' ? checked : value;

    if (name === 'guests') {
      const parsed = parseInt(value, 10);
      newValue = Number.isNaN(parsed) ? '' : parsed;
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleSalaChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const salaId = e.target.value;
    setFormData(prev => ({
      ...prev,
      sala_id: salaId,
      turno: undefined,
      horario_id: '',
      time: '',
      tableId: '',
      tableIds: []
    }));

    // Limpar validações anteriores
    setGuestValidationError('');
    setGuestCapacityWarning(null);
    setGuestCapacityWarningConfirmed(false);

    if (salaId) {
      // Carregar informações da sala e horários em paralelo
      await Promise.all([
        loadSalaInfo(salaId),
        loadAvailableShiftsAndTimes(salaId)
      ]);
    } else {
      setSalaInfo(null);
      clearShiftState();
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const optionValue = e.target.value;
    const selectedOption = resolveSelectedTimeOption(optionValue);

    setFormData(prev => ({
      ...prev,
      turno: selectedOption?.turno ?? undefined,
      horario_id: selectedOption?.horario_id ?? null,
      time: selectedOption?.time ?? ''
    }));
  };

  const handleTableChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const tableIds = Array.from(e.target.selectedOptions).map((option) => option.value);
    const selectedTable = allTables.find(t => String(t.id) === String(tableIds[0]));
    setFormData(prev => ({
      ...prev,
      tableId: tableIds[0] ?? '',
      tableIds,
      sala_id: selectedTable?.floorId ?? prev.sala_id
    }));
  };

  return (
    <div className="reservation-form-overlay">
      <div className="reservation-form">
        <ReservationFormHeader isEditing={isEditing} onClose={onClose} />
        
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <ReservationFormScheduleFields
              isEditing={isEditing}
              date={formData.date}
              maxDateValue={maxDateValue}
              onDateChange={handleDateChange}
              salaId={formData.sala_id}
              floors={floors}
              onSalaChange={handleSalaChange}
              availableTimeOptions={availableTimeOptions}
              selectedTimeValue={selectedTimeValue}
              loadingShifts={loadingShifts}
              onTimeChange={handleTimeChange}
              blockedShiftNotice={blockedShiftNotice}
              noAvailableShiftsMessage={noAvailableShiftsMessage}
            />

            <ReservationFormCapacityFields
              time={formData.time}
              guests={formData.guests}
              onGuestsChange={handleChange}
              showGuestLimits={Boolean(salaInfo && !salaUsaMesas)}
              minGuestsLimit={minGuestsLimit}
              maxGuestsLimit={maxGuestsLimit}
              salaUsaMesas={salaUsaMesas}
              guestValidationError={guestValidationError}
              guestCapacityWarning={guestCapacityWarning}
              guestCapacityWarningConfirmed={guestCapacityWarningConfirmed}
              onGuestCapacityWarningConfirmedChange={setGuestCapacityWarningConfirmed}
              tableIds={formData.tableIds}
              onTableChange={handleTableChange}
              tablesForSala={allTables.filter((table) => table.floorId === formData.sala_id)}
              reservedTableIds={reservedTableIdsForSelectedSlot}
              tableAvailabilityMessage={tableAvailabilityMessage}
              tableAvailabilityStatus={tableAvailabilityStatus}
            />

            <ReservationFormClientFields
              time={formData.time}
              isEditing={isEditing}
              name={formData.name}
              phone={formData.phone}
              email={formData.email}
              birthdate={formData.birthdate}
              notes={formData.notes}
              onChange={handleChange}
              onOpenCrmSearch={() => openCrmSearch(formData.name || formData.phone || '')}
            />
          </div>
          
          <ReservationFormActions
            isEditing={isEditing}
            isSaving={isSavingReservation}
            saveDisabled={
              isSavingReservation
              || !!guestValidationError
              || !!(guestCapacityWarning && !guestCapacityWarningConfirmed)
            }
            saveTitle={guestValidationError || guestCapacityWarning?.message || ''}
            onCancelClick={() => setShowCancelConfirm(true)}
          />
        </form>
        {!isEditing && (
          <ReservationFormCrmSearchModal
            open={crmSearchOpen}
            searchTerm={crmSearchTerm}
            onSearchTermChange={setCrmSearchTerm}
            results={crmSearchResults}
            loading={crmSearchLoading}
            error={crmSearchError}
            onClose={closeCrmSearch}
            onSearch={handleCrmSearch}
            onSelect={selectCrmCustomer}
          />
        )}
        {!isEditing && (
          <ReservationFormClientConfirmationModal
            open={showClientConfirmationChoice}
            customerName={formData.name}
            date={formData.date}
            time={formData.time}
            hasPhone={pendingReservationHasPhone}
            isSaving={isSavingReservation}
            onClose={closeClientConfirmationChoiceModal}
            onChoose={handleClientConfirmationChoice}
          />
        )}
        {isEditing && (
          <ReservationFormCancelConfirmModal
            open={showCancelConfirm}
            customerName={formData.name || reservation?.nome_cliente || 'cliente'}
            date={formData.date}
            time={formData.time || (reservation?.hora ? reservation.hora.split(':').slice(0, 2).join(':') : '')}
            isCanceling={isCanceling}
            onClose={() => setShowCancelConfirm(false)}
            onConfirm={handleCancelReservation}
          />
        )}
      </div>
    </div>
  );
};

export default ReservationForm;
